import { List } from "immutable";
import AppConstants from "@/constants/AppConstants";

const { ELEMENT_TYPE_TEXT, ELEMENT_TYPE_BUTTON } = AppConstants;

/**
 * Minimum vertical overlap (as a fraction of the shorter element's height)
 * required for two elements to be considered in the same row.
 */
export const ROW_OVERLAP_THRESHOLD = 0.5;

const READABLE_TYPES = new Set([ELEMENT_TYPE_TEXT, ELEMENT_TYPE_BUTTON]);

const isReadable = (element) => READABLE_TYPES.has(element.get("type"));

const getRect = (element, layout) => ({
  x: Number(element.getIn([layout, "x"]) ?? 0),
  y: Number(element.getIn([layout, "y"]) ?? 0),
  w: Number(element.getIn([layout, "w"]) ?? 0),
  h: Number(element.getIn([layout, "h"]) ?? 0),
});

/**
 * Two rects share a row if their vertical spans overlap by at least
 * ROW_OVERLAP_THRESHOLD * min(h1, h2).
 */
const sharesRow = (rectA, rectB) => {
  const overlap = Math.min(rectA.y + rectA.h, rectB.y + rectB.h) - Math.max(rectA.y, rectB.y);
  if (overlap <= 0) return false;
  const shorter = Math.min(rectA.h, rectB.h);
  if (shorter <= 0) return false;
  return overlap >= ROW_OVERLAP_THRESHOLD * shorter;
};

const rectsOverlap = (a, b) =>
  a.x < b.x + b.w && a.x + a.w > b.x && a.y < b.y + b.h && a.y + a.h > b.y;

/**
 * Ensures no text/button is covered by a shape that was originally BELOW it
 * (lower array index) but ended up above it after chunk reordering. Shapes
 * that were already above the text in the original array represent intentional
 * layering (e.g., hover overlays) and are left alone.
 *
 * @param {Array<import("immutable").Map>} arr - mutated in place
 * @param {Array<{x:number,y:number,w:number,h:number}>} rects - parallel to arr
 * @param {Array<boolean>} readable - parallel to arr
 * @param {Map<import("immutable").Map, number>} originalIndices
 */
const promoteReadableAboveOverlaps = (arr, rects, readable, originalIndices) => {
  for (let i = 0; i < arr.length; i++) {
    if (!readable[i]) continue;

    const textRect = rects[i];
    const textOrigIdx = originalIndices.get(arr[i]);
    let promoteToAfter = -1;

    for (let j = i + 1; j < arr.length; j++) {
      if (readable[j]) continue;
      if (
        originalIndices.get(arr[j]) < textOrigIdx &&
        rectsOverlap(textRect, rects[j])
      ) {
        promoteToAfter = j;
      }
    }

    if (promoteToAfter > i) {
      const [textEl] = arr.splice(i, 1);
      const [textRectVal] = rects.splice(i, 1);
      const [textReadable] = readable.splice(i, 1);
      arr.splice(promoteToAfter, 0, textEl);
      rects.splice(promoteToAfter, 0, textRectVal);
      readable.splice(promoteToAfter, 0, textReadable);
      i--;
    }
  }
};

/**
 * Row-banding sort: group items into rows by vertical overlap, sort
 * left-to-right within each row, then sort rows top-to-bottom.
 */
const rowBandingSort = (items) => {
  if (items.length <= 1) return items;

  const sorted = [...items].sort((a, b) => a.rect.y - b.rect.y || a.originalIndex - b.originalIndex);

  const rows = [];
  for (const item of sorted) {
    let placed = false;
    for (const row of rows) {
      if (row.every((member) => sharesRow(item.rect, member.rect))) {
        row.push(item);
        placed = true;
        break;
      }
    }
    if (!placed) rows.push([item]);
  }

  for (const row of rows) {
    row.sort((a, b) => a.rect.x - b.rect.x || a.originalIndex - b.originalIndex);
  }

  rows.sort((a, b) => {
    const minYA = Math.min(...a.map((m) => m.rect.y));
    const minYB = Math.min(...b.map((m) => m.rect.y));
    return minYA - minYB;
  });

  return rows.flat();
};

/**
 * Sort a list of elements so readable content (text and button elements)
 * appears in top-to-bottom, left-to-right reading order.
 *
 * Non-readable elements (shapes, graphics, etc.) are NOT sorted individually.
 * Instead they travel with the readable element that follows them in the
 * array — preserving relative z-layering within each "chunk."
 *
 * The layers panel renders via `unshift`, so the array is the reverse of
 * what the user sees in the panel. In the panel, shapes sit BELOW their
 * text; in the array, that means shapes are at LOWER indices (before the
 * text). A chunk is therefore: zero or more leading non-readable elements
 * followed by one readable element (text/button).
 *
 * Non-readable elements after the LAST readable element in the array form
 * a suffix that lands at the end of the output (top of the layers panel).
 *
 * The output is reversed so the first chunk in reading order (top-left on
 * canvas) lands at the end of the array (top of the layers panel).
 *
 * @param {import("immutable").List} elements
 * @param {"desktop" | "mobile"} layout
 * @returns {import("immutable").List}
 */
export const sortByReadingOrder = (elements, layout) => {
  if (!elements || elements.size === 0) return List();
  if (elements.size === 1) return elements;

  const chunks = [];
  let buffer = [];

  elements.forEach((element) => {
    if (isReadable(element)) {
      chunks.push({
        leading: buffer,
        leader: element,
        rect: getRect(element, layout),
      });
      buffer = [];
    } else {
      buffer.push(element);
    }
  });

  const suffix = buffer;

  if (chunks.length === 0) return elements;

  const sortedItems = rowBandingSort(
    chunks.map((chunk, i) => ({
      chunk,
      rect: chunk.rect,
      originalIndex: i,
    }))
  );

  // Flatten in reverse so the first-read chunk lands at the end of the array
  // (top of the layers panel). Suffix at the very end.
  const flat = [];
  for (let i = sortedItems.length - 1; i >= 0; i--) {
    const { chunk } = sortedItems[i];
    for (const el of chunk.leading) flat.push(el);
    flat.push(chunk.leader);
  }
  for (const el of suffix) flat.push(el);

  const originalIndices = new Map();
  elements.forEach((el, idx) => originalIndices.set(el, idx));

  // Pre-compute rects and readable flags once so the overlap pass isn't
  // doing O(n²) Immutable traversals.
  const rects = flat.map((el) => getRect(el, layout));
  const readable = flat.map(isReadable);

  promoteReadableAboveOverlaps(flat, rects, readable, originalIndices);

  return List(flat);
};
